import React, { useState } from 'react';
import axios from 'axios';
import { Search, X, Sparkles, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BAG_TYPE_OPTIONS = ['any', 'bag', 'trolley', 'suitcase', 'backpack', 'duffle', 'box', 'other'];
const MATERIAL_OPTIONS = ['any', 'soft_fabric', 'hard_shell', 'leather', 'plastic'];
const LOCK_OPTIONS = ['any', 'tsa', 'combination', 'key', 'none'];

/**
 * Admin support tool — given a customer's verbal description of a bag,
 * search every booking in the system for matching bags. All fields are
 * optional; at least one must be provided. Results are streamed back
 * without the (heavy) base64 images to keep payload small.
 */
export const FindBagPanel = () => {
  const [q, setQ] = useState({ color: '', brand: '', bag_type: 'any', lock_type: 'any', material: 'any', marks: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null); // null = never searched, [] = no results

  const canSearch =
    q.color.trim() || q.brand.trim() || q.marks.trim() ||
    q.bag_type !== 'any' || q.lock_type !== 'any' || q.material !== 'any';

  const runSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (q.color.trim()) params.color = q.color.trim();
      if (q.brand.trim()) params.brand = q.brand.trim();
      if (q.marks.trim()) params.marks = q.marks.trim();
      if (q.bag_type !== 'any') params.bag_type = q.bag_type;
      if (q.lock_type !== 'any') params.lock_type = q.lock_type;
      if (q.material !== 'any') params.material = q.material;
      const res = await axios.get(`${API}/admin/bookings/find-by-bag`, { params });
      setResults(res.data.results || []);
    } catch (err) {
      console.error('[admin] find-by-bag failed:', err);
      setError(err?.response?.data?.detail || 'Search failed');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const resetSearch = () => {
    setQ({ color: '', brand: '', bag_type: 'any', lock_type: 'any', material: 'any', marks: '' });
    setResults(null);
    setError('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') runSearch();
  };

  return (
    <div data-testid="find-bag-panel" className="border-2 border-black bg-white">
      <div className="px-4 py-3 bg-black text-white flex items-center gap-2">
        <Search size={16} />
        <span className="font-mono uppercase text-xs font-bold tracking-wider">Find a bag by description</span>
        <span className="ml-auto text-[10px] font-mono opacity-70">Uses AI-detected attributes on every booking</span>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Color</label>
            <input
              data-testid="fb-color"
              type="text"
              value={q.color}
              onChange={(e) => setQ({ ...q, color: e.target.value })}
              onKeyDown={onKeyDown}
              placeholder="e.g. navy, orange"
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Brand</label>
            <input
              data-testid="fb-brand"
              type="text"
              value={q.brand}
              onChange={(e) => setQ({ ...q, brand: e.target.value })}
              onKeyDown={onKeyDown}
              placeholder="e.g. Samsonite"
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Bag type</label>
            <select
              data-testid="fb-type"
              value={q.bag_type}
              onChange={(e) => setQ({ ...q, bag_type: e.target.value })}
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono uppercase"
            >
              {BAG_TYPE_OPTIONS.map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Material</label>
            <select
              data-testid="fb-material"
              value={q.material}
              onChange={(e) => setQ({ ...q, material: e.target.value })}
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono uppercase"
            >
              {MATERIAL_OPTIONS.map(o => <option key={o} value={o}>{o.toUpperCase().replace('_', ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Lock type</label>
            <select
              data-testid="fb-lock"
              value={q.lock_type}
              onChange={(e) => setQ({ ...q, lock_type: e.target.value })}
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono uppercase"
            >
              {LOCK_OPTIONS.map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase font-bold text-gray-600 mb-1">Distinguishing marks</label>
            <input
              data-testid="fb-marks"
              type="text"
              value={q.marks}
              onChange={(e) => setQ({ ...q, marks: e.target.value })}
              onKeyDown={onKeyDown}
              placeholder="e.g. red ribbon, yellow sticker"
              className="w-full border-2 border-black px-2 py-1.5 text-sm font-mono"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            data-testid="fb-search-btn"
            onClick={runSearch}
            disabled={!canSearch || loading}
            className={`px-4 py-2 border-2 border-black font-mono uppercase text-xs font-bold flex items-center gap-2 ${
              canSearch && !loading ? 'bg-[#FF4500] text-black hover:bg-black hover:text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Search size={14} />
            {loading ? 'Searching…' : 'Search bookings'}
          </button>
          <button
            data-testid="fb-reset-btn"
            onClick={resetSearch}
            className="px-3 py-2 border-2 border-black bg-white hover:bg-gray-100 font-mono uppercase text-xs font-bold flex items-center gap-1"
          >
            <X size={14} /> Reset
          </button>
          {results !== null && !loading && !error && (
            <span data-testid="fb-count" className="text-xs font-mono text-gray-600 ml-2">
              {results.length} matching booking{results.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        {error && (
          <div data-testid="fb-error" className="p-2 border-2 border-red-500 bg-red-50 text-xs font-mono text-red-700">{error}</div>
        )}
      </div>

      {/* Results */}
      {results !== null && !loading && !error && results.length > 0 && (
        <div className="border-t-2 border-black">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px]">Booking</th>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px]">Customer</th>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px]">Route</th>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px]">Status</th>
                  <th className="text-left px-3 py-2 font-mono uppercase text-[10px]">Matched bag</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  const bag = (r.matched_bags && r.matched_bags[0]) || {};
                  return (
                    <tr key={r.booking_id} data-testid={`fb-row-${r.booking_id}`} className="border-t border-gray-200 hover:bg-gray-50 align-top">
                      <td className="px-3 py-2 font-mono text-xs">
                        <div className="font-bold">{r.baggage_tag || '—'}</div>
                        <div className="text-[10px] text-gray-500">{r.booking_id?.slice(0, 8)}…</div>
                        <div className="text-[10px] text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="font-bold">{r.customer_name || '—'}</div>
                        <div className="font-mono text-[10px] text-gray-500 truncate max-w-[140px]">{r.user_id?.slice(0, 8)}…</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-1"><MapPin size={10} className="text-[#FF4500]" />{r.pickup_location}</div>
                        <div className="flex items-center gap-1 text-gray-600 mt-0.5">↓ {r.delivery_location}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="inline-block px-2 py-0.5 bg-black text-white font-mono uppercase text-[10px]">{r.tracking_status || '—'}</span>
                        {r.payment_status === 'paid' && <div className="text-[10px] font-mono text-green-700 mt-1">PAID</div>}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-bold uppercase">{bag.bag_type}</span>
                          {bag.ai_detected && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-green-600 bg-green-50 text-green-800 text-[9px] font-mono uppercase">
                              <Sparkles size={9} /> AI · {bag.ai_confidence || 'medium'}
                            </span>
                          )}
                        </div>
                        <div className="text-gray-700 mt-0.5">
                          {[bag.color, bag.brand, bag.material?.replace('_', ' '), bag.lock_type && `${bag.lock_type} lock`].filter(Boolean).join(' · ')}
                        </div>
                        {bag.distinguishing_marks && (
                          <div className="text-[10px] text-gray-500 italic mt-0.5">{bag.distinguishing_marks}</div>
                        )}
                        {r.total_bags > 1 && r.matched_bags.length < r.total_bags && (
                          <div className="text-[10px] text-gray-400 mt-0.5">(+{r.total_bags - r.matched_bags.length} other bag{r.total_bags - r.matched_bags.length === 1 ? '' : 's'} in this booking)</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {results !== null && !loading && !error && results.length === 0 && (
        <div data-testid="fb-empty" className="border-t-2 border-black p-6 text-center font-mono text-xs text-gray-500">
          No bookings match those attributes. Try loosening the filters.
        </div>
      )}
    </div>
  );
};

export default FindBagPanel;
